import React, { useState, useEffect, useRef } from 'react';
import { 
  ChevronDown, 
  Menu, 
  X, 
  ArrowRight, 
  BookOpen, 
  FileText, 
  HelpCircle, 
  Sparkles, 
  Workflow, 
  Mail, 
  LayoutDashboard, 
  Share2, 
  Calendar, 
  BarChart3, 
  Brain, 
  Users, 
  HardDrive, 
  Bot, 
  ShieldCheck, 
  Video, 
  CheckCircle2, 
  Globe2, 
  Building2, 
  Laptop2, 
  ShoppingBag,
  Flame,
  MessageSquare,
  MessageCircle,
  Send,
  GitBranch,
  UserCheck
} from 'lucide-react';
import { ThemeToggle } from '../ThemeToggle';
import { ScaleBulkLogo } from '../brand/ScaleBulkLogo';

interface PublicHeaderProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  onOpenAuth: (mode: 'login' | 'register') => void;
  isAuthenticated?: boolean;
}

type MegaMenuKey = 'features' | 'solutions' | 'integrations' | 'whatsapp' | 'resources' | null;

export const PublicHeader: React.FC<PublicHeaderProps> = ({
  currentPath,
  onNavigate,
  onOpenAuth,
  isAuthenticated
}) => {
  const isLoggedIn = isAuthenticated !== undefined
    ? isAuthenticated
    : (typeof window !== 'undefined' ? Boolean(localStorage.getItem('scalebulk_token')) : false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeMenu, setActiveMenu] = useState<MegaMenuKey>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpandedSection, setMobileExpandedSection] = useState<string | null>(null);
  
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [mobileMenuOpen]);

  // Close mega menu on Escape key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActiveMenu(null);
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleNavClick = (path: string) => {
    onNavigate(path);
    setActiveMenu(null);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMouseEnter = (menuKey: MegaMenuKey) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveMenu(menuKey);
  };

  const handleMouseLeave = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setActiveMenu(null);
    }, 180);
  };

  const isWhatsAppActive = currentPath.startsWith('/whatsapp') || currentPath.startsWith('/bulk-whatsapp-') || currentPath.startsWith('/multi-agent-');

  return (
    <>
      <header
        className={`public-header-glass ${isScrolled ? 'scrolled' : ''}`}
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 300,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          padding: isScrolled ? '7px 0' : '11px 0',
          transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
        onMouseLeave={handleMouseLeave}
      >
        <div style={{ maxWidth: '1280px', width: '100%', margin: '0 auto', padding: '0 clamp(12px, 3.5vw, 24px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', boxSizing: 'border-box' }}>
          
          {/* Left: Brand Logo */}
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); handleNavClick('/'); }}
            style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', flexShrink: 1, minWidth: 0 }}
            aria-label="Scale Bulk Homepage"
          >
            <ScaleBulkLogo size="md" priority={true} />
          </a>

          {/* Center: Desktop Navigation Bar with Professional Mega Menu Triggers */}
          <nav className="desktop-nav" aria-label="Main Navigation">
            
            {/* 1. Features Trigger */}
            <div
              style={{ position: 'relative' }}
              onMouseEnter={() => handleMouseEnter('features')}
            >
              <button
                onClick={() => setActiveMenu(activeMenu === 'features' ? null : 'features')}
                aria-expanded={activeMenu === 'features'}
                style={{
                  background: activeMenu === 'features' ? 'var(--bg-glass-active)' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.88rem',
                  fontWeight: currentPath.startsWith('/features') || activeMenu === 'features' ? 700 : 500,
                  color: activeMenu === 'features' || currentPath.startsWith('/features') ? 'var(--primary)' : 'var(--text-secondary)',
                  padding: '7px 12px',
                  borderRadius: 'var(--radius-sm)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all var(--transition-fast)'
                }}
              >
                <span>Features</span>
                <ChevronDown 
                  size={13} 
                  style={{ 
                    transform: activeMenu === 'features' ? 'rotate(180deg)' : 'rotate(0)', 
                    transition: 'transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                    opacity: 0.7
                  }} 
                />
              </button>
            </div>

            {/* 2. Solutions Trigger */}
            <div
              style={{ position: 'relative' }}
              onMouseEnter={() => handleMouseEnter('solutions')}
            >
              <button
                onClick={() => setActiveMenu(activeMenu === 'solutions' ? null : 'solutions')}
                aria-expanded={activeMenu === 'solutions'}
                style={{
                  background: activeMenu === 'solutions' ? 'var(--bg-glass-active)' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.88rem',
                  fontWeight: currentPath.startsWith('/solutions') || activeMenu === 'solutions' ? 700 : 500,
                  color: activeMenu === 'solutions' || currentPath.startsWith('/solutions') ? 'var(--primary)' : 'var(--text-secondary)',
                  padding: '7px 12px',
                  borderRadius: 'var(--radius-sm)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all var(--transition-fast)'
                }}
              >
                <span>Solutions</span>
                <ChevronDown 
                  size={13} 
                  style={{ 
                    transform: activeMenu === 'solutions' ? 'rotate(180deg)' : 'rotate(0)', 
                    transition: 'transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                    opacity: 0.7
                  }} 
                />
              </button>
            </div>

            {/* 3. Integrations Trigger */}
            <div
              style={{ position: 'relative' }}
              onMouseEnter={() => handleMouseEnter('integrations')}
            >
              <button
                onClick={() => setActiveMenu(activeMenu === 'integrations' ? null : 'integrations')}
                aria-expanded={activeMenu === 'integrations'}
                style={{
                  background: activeMenu === 'integrations' ? 'var(--bg-glass-active)' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.88rem',
                  fontWeight: currentPath.startsWith('/integrations') || activeMenu === 'integrations' ? 700 : 500,
                  color: activeMenu === 'integrations' || currentPath.startsWith('/integrations') ? 'var(--primary)' : 'var(--text-secondary)',
                  padding: '7px 12px',
                  borderRadius: 'var(--radius-sm)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all var(--transition-fast)'
                }}
              >
                <span>Integrations</span>
                <ChevronDown 
                  size={13} 
                  style={{ 
                    transform: activeMenu === 'integrations' ? 'rotate(180deg)' : 'rotate(0)', 
                    transition: 'transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                    opacity: 0.7
                  }} 
                />
              </button>
            </div>

            {/* 4. WhatsApp (Short, Clean & Professional Mega Trigger) */}
            <div
              style={{ position: 'relative' }}
              onMouseEnter={() => handleMouseEnter('whatsapp')}
            >
              <button
                onClick={() => handleNavClick('/whatsapp')}
                aria-expanded={activeMenu === 'whatsapp'}
                style={{
                  background: isWhatsAppActive || activeMenu === 'whatsapp' ? 'rgba(37, 211, 102, 0.12)' : 'transparent',
                  border: isWhatsAppActive || activeMenu === 'whatsapp' ? '1px solid rgba(37, 211, 102, 0.28)' : '1px solid transparent',
                  cursor: 'pointer',
                  fontSize: '0.88rem',
                  fontWeight: isWhatsAppActive || activeMenu === 'whatsapp' ? 700 : 600,
                  color: isWhatsAppActive || activeMenu === 'whatsapp' ? 'var(--whatsapp-text)' : 'var(--text-secondary)',
                  padding: '6px 12px',
                  borderRadius: 'var(--radius-sm)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all var(--transition-fast)'
                }}
              >
                <span>WhatsApp</span>
                <ChevronDown 
                  size={13} 
                  style={{ 
                    transform: activeMenu === 'whatsapp' ? 'rotate(180deg)' : 'rotate(0)', 
                    transition: 'transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                    opacity: 0.7
                  }} 
                />
              </button>
            </div>

            {/* 5. Resources Trigger */}
            <div
              style={{ position: 'relative' }}
              onMouseEnter={() => handleMouseEnter('resources')}
            >
              <button
                onClick={() => setActiveMenu(activeMenu === 'resources' ? null : 'resources')}
                aria-expanded={activeMenu === 'resources'}
                style={{
                  background: activeMenu === 'resources' ? 'var(--bg-glass-active)' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.88rem',
                  fontWeight: activeMenu === 'resources' ? 700 : 500,
                  color: activeMenu === 'resources' ? 'var(--primary)' : 'var(--text-secondary)',
                  padding: '7px 12px',
                  borderRadius: 'var(--radius-sm)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all var(--transition-fast)'
                }}
              >
                <span>Resources</span>
                <ChevronDown 
                  size={13} 
                  style={{ 
                    transform: activeMenu === 'resources' ? 'rotate(180deg)' : 'rotate(0)', 
                    transition: 'transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                    opacity: 0.7
                  }} 
                />
              </button>
            </div>

            {/* 6. Pricing Link */}
            <a
              href="/pricing"
              onClick={(e) => { e.preventDefault(); handleNavClick('/pricing'); }}
              style={{
                textDecoration: 'none',
                fontSize: '0.88rem',
                fontWeight: currentPath === '/pricing' ? 700 : 500,
                color: currentPath === '/pricing' ? 'var(--primary)' : 'var(--text-secondary)',
                padding: '7px 12px',
                borderRadius: 'var(--radius-sm)',
                transition: 'color var(--transition-fast)'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-primary)')}
              onMouseLeave={(e) => {
                if (currentPath !== '/pricing') e.currentTarget.style.color = 'var(--text-secondary)';
              }}
            >
              Pricing
            </a>
          </nav>

          {/* Right: Actions & Theme Toggle */}
          <div className="desktop-actions">
            <ThemeToggle />

            {isLoggedIn ? (
              <button
                onClick={() => handleNavClick('/dashboard')}
                className="btn-magic"
                style={{ padding: '8px 18px', fontSize: '0.88rem' }}
              >
                <LayoutDashboard size={15} />
                <span>Dashboard</span>
                <ArrowRight size={14} />
              </button>
            ) : (
              <>
                <button
                  onClick={() => onOpenAuth('login')}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-primary)',
                    fontSize: '0.88rem',
                    fontWeight: 600,
                    padding: '8px 14px',
                    borderRadius: 'var(--radius-md)',
                    transition: 'color var(--transition-fast)'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--primary)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-primary)')}
                >
                  Log In
                </button>

                <button
                  onClick={() => onOpenAuth('register')}
                  className="btn-magic"
                  style={{ padding: '8px 18px', fontSize: '0.88rem' }}
                >
                  <span>Start Free</span>
                  <ArrowRight size={14} />
                </button>
              </>
            )}
          </div>

          {/* Mobile Right Bar: Theme Toggle + Hamburger */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }} className="mobile-actions-bar">
            <div className="mobile-theme-toggle">
              <ThemeToggle />
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle navigation menu"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '44px',
                height: '44px',
                minWidth: '44px',
                minHeight: '44px',
                background: 'var(--bg-glass)',
                border: '1px solid var(--border-color)',
                color: 'var(--text-primary)',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer'
              }}
              className="mobile-hamburger-btn"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>

          {/* ========================================================================= */}
          {/* DESKTOP MEGA MENU FLOATING OVERLAYS */}
          {/* ========================================================================= */}
          {activeMenu && (
            <div
              className="desktop-mega-menu hide-on-mobile"
              onMouseEnter={() => handleMouseEnter(activeMenu)}
              onMouseLeave={handleMouseLeave}
              style={{
                position: 'absolute',
                top: 'calc(100% + 10px)',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 'min(94vw, 1120px)',
                background: 'var(--bg-menu-surface, #0b0f19)',
                backdropFilter: 'blur(36px) saturate(200%)',
                WebkitBackdropFilter: 'blur(36px) saturate(200%)',
                border: '1px solid var(--border-color)',
                borderRadius: '20px',
                boxShadow: '0 28px 80px -12px rgba(0, 0, 0, 0.75), 0 0 0 1px rgba(255, 255, 255, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                padding: '28px 32px',
                zIndex: 350,
                animation: 'scaleBulkMegaMenuIn 0.22s cubic-bezier(0.16, 1, 0.3, 1)'
              }}
            >
              {/* 1. FEATURES MEGA MENU */}
              {activeMenu === 'features' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1fr', gap: '32px', alignItems: 'start' }}>
                  {/* Column 1: Core Creation */}
                  <div>
                    <div style={{ fontSize: '0.74rem', fontWeight: 800, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '14px' }}>
                      Publishing &amp; AI Studio
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <MegaMenuItem
                        icon={Sparkles}
                        title="AI Content Studio"
                        desc="Adapts hooks, length, and hashtags for each network."
                        onClick={() => handleNavClick('/features/ai-content-studio')}
                      />
                      <MegaMenuItem
                        icon={Share2}
                        title="One-Click Multi-Publishing"
                        desc="Simultaneous dispatch to 7 networks via verified APIs."
                        badge="Zero Push"
                        onClick={() => handleNavClick('/features/one-click-publishing')}
                      />
                      <MegaMenuItem
                        icon={Calendar}
                        title="Smart Queue Scheduling"
                        desc="Algorithmic peak engagement time window queues."
                        onClick={() => handleNavClick('/features/smart-scheduling')}
                      />
                      <MegaMenuItem
                        icon={BarChart3}
                        title="Multi-Channel Analytics"
                        desc="Unified performance, virality score, and audience reach."
                        onClick={() => handleNavClick('/features/multi-channel-analytics')}
                      />
                    </div>
                  </div>

                  {/* Column 2: Management & Infrastructure */}
                  <div>
                    <div style={{ fontSize: '0.74rem', fontWeight: 800, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '14px' }}>
                      Workspaces &amp; Automation
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <MegaMenuItem
                        icon={Brain}
                        title="Brand Memory Profiles"
                        desc="Train unique tone, vocabulary rules, and guardrails."
                        onClick={() => handleNavClick('/features/brand-memory-profiles')}
                      />
                      <MegaMenuItem
                        icon={Users}
                        title="Agency Client Workspaces"
                        desc="Multi-tenant client isolation with approval gates."
                        badge="Popular"
                        onClick={() => handleNavClick('/solutions/client-workspace-isolation')}
                      />
                      <MegaMenuItem
                        icon={HardDrive}
                        title="Media Cloud &amp; Drive Sync"
                        desc="Video transcoding and Google Drive two-way sync."
                        onClick={() => handleNavClick('/features/media-cloud-drive-sync')}
                      />
                      <MegaMenuItem
                        icon={Bot}
                        title="Model Context Protocol (MCP)"
                        desc="AI agent automation bus for programmatic dispatch."
                        badge="API v2"
                        onClick={() => handleNavClick('/developers/mcp-api-v2')}
                      />
                    </div>
                  </div>

                  {/* Column 3: Featured Spotlight Card */}
                  <div style={{ padding: '22px', borderRadius: '18px', background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(6, 182, 212, 0.06))', border: '1px solid rgba(59, 130, 246, 0.25)' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: 'var(--radius-full)', background: 'rgba(59, 130, 246, 0.2)', marginBottom: '12px' }}>
                      <Flame size={13} color="var(--primary)" />
                      <span style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--primary-light)' }}>SCALE BULK 3.0</span>
                    </div>
                    <h4 style={{ fontSize: '1.05rem', fontWeight: 800, margin: '0 0 8px', color: 'var(--text-primary)' }}>
                      7 Networks Synced Concurrently
                    </h4>
                    <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.55, margin: '0 0 16px' }}>
                      See how Scale Bulk adapts a single prompt into platform-native Instagram Reels, LinkedIn carousels, and TikToks.
                    </p>
                    <button
                      onClick={() => handleNavClick('/features')}
                      className="btn-magic"
                      style={{ width: '100%', padding: '9px 14px', fontSize: '0.84rem', justifyContent: 'center' }}
                    >
                      <span>Explore All Features</span>
                      <ArrowRight size={14} />
                    </button>
                  </div>
                </div>
              )}

              {/* 2. SOLUTIONS MEGA MENU */}
              {activeMenu === 'solutions' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1fr', gap: '32px', alignItems: 'start' }}>
                  <div>
                    <div style={{ fontSize: '0.74rem', fontWeight: 800, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '14px' }}>
                      By Role &amp; Organization
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <MegaMenuItem
                        icon={Building2}
                        title="Marketing &amp; Digital Agencies"
                        desc="Manage 20+ clients with segregated vaults and approval gates."
                        badge="Agencies"
                        onClick={() => handleNavClick('/solutions/marketing-digital-agencies')}
                      />
                      <MegaMenuItem
                        icon={Laptop2}
                        title="Content Creators &amp; Influencers"
                        desc="Repurpose 1 video into Reels, Shorts, and TikToks in seconds."
                        onClick={() => handleNavClick('/solutions/content-creators-influencers')}
                      />
                      <MegaMenuItem
                        icon={ShoppingBag}
                        title="D2C &amp; E-Commerce Brands"
                        desc="Coordinate product launches and catalog carousel drops."
                        onClick={() => handleNavClick('/solutions/small-business')}
                      />
                      <MegaMenuItem
                        icon={ShieldCheck}
                        title="B2B &amp; Enterprise Teams"
                        desc="Executive personal branding with strict GDPR/SOC2 security."
                        onClick={() => handleNavClick('/solutions/b2b-enterprise-teams')}
                      />
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '0.74rem', fontWeight: 800, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '14px' }}>
                      By Strategic Workflow
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <MegaMenuItem
                        icon={Video}
                        title="Short-Form Video Engine"
                        desc="Direct 9:16 vertical video auto-publishing without phone apps."
                        onClick={() => handleNavClick('/solutions/social-automation')}
                      />
                      <MegaMenuItem
                        icon={CheckCircle2}
                        title="Client Approval Gateways"
                        desc="Shareable client review links with 1-click approvals."
                        onClick={() => handleNavClick('/solutions/client-workspace-isolation')}
                      />
                      <MegaMenuItem
                        icon={Workflow}
                        title="Cross-Network Repurposing"
                        desc="AI adapts character budgets and formatting per channel."
                        onClick={() => handleNavClick('/how-it-works')}
                      />
                      <MegaMenuItem
                        icon={Globe2}
                        title="Global Multi-Timezone Drops"
                        desc="Coast-to-coast US, UK, EU, UAE, and APAC calibration."
                        onClick={() => handleNavClick('/solutions')}
                      />
                    </div>
                  </div>

                  <div style={{ padding: '22px', borderRadius: '18px', background: 'rgba(255, 255, 255, 0.03)', border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '10px' }}>
                      The Scale Bulk Advantage
                    </div>
                    <h4 style={{ fontSize: '1rem', fontWeight: 800, margin: '0 0 8px', color: 'var(--text-primary)' }}>
                      Why Teams Switch from Legacy Tools
                    </h4>
                    <ul style={{ paddingLeft: '18px', fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 16px' }}>
                      <li>100% direct official APIs (no mobile reminders)</li>
                      <li>AES-256-GCM encrypted credential vault</li>
                      <li>Isolated multi-client agency workspaces</li>
                      <li>Transparent pricing without per-channel penalties</li>
                    </ul>
                    <button
                      onClick={() => handleNavClick('/solutions')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--primary)',
                        fontSize: '0.84rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: 0
                      }}
                    >
                      <span>View All Solutions</span>
                      <ArrowRight size={13} />
                    </button>
                  </div>
                </div>
              )}

              {/* 3. INTEGRATIONS MEGA MENU */}
              {activeMenu === 'integrations' && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
                    <div>
                      <h3 style={{ fontSize: '1.15rem', fontWeight: 800, margin: 0, color: 'var(--text-primary)' }}>
                        7 Official Developer Graph &amp; REST Integrations
                      </h3>
                      <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                        Official OAuth 2.0 PKCE authentication with zero unauthorized browser scraping.
                      </p>
                    </div>
                    <button
                      onClick={() => handleNavClick('/integrations')}
                      style={{
                        background: 'rgba(59, 130, 246, 0.1)',
                        border: '1px solid rgba(59, 130, 246, 0.25)',
                        color: 'var(--primary)',
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        padding: '6px 14px',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      View All Integrations →
                    </button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
                    <IntegrationCard
                      name="Instagram"
                      desc="Reels, image & carousel publishing workflows"
                      tag="Graph API"
                      color="var(--color-instagram)"
                      onClick={() => handleNavClick('/integrations/instagram')}
                    />
                    <IntegrationCard
                      name="Facebook"
                      desc="Supported Page text, image & video publishing"
                      tag="Pages API"
                      color="var(--color-facebook)"
                      onClick={() => handleNavClick('/integrations/facebook')}
                    />
                    <IntegrationCard
                      name="LinkedIn"
                      desc="Supported member accounts & company Pages"
                      tag="Posts API"
                      color="var(--color-linkedin)"
                      onClick={() => handleNavClick('/integrations/linkedin')}
                    />
                    <IntegrationCard
                      name="X (Twitter)"
                      desc="Supported posts, media, threads & polls"
                      tag="API v2"
                      color="var(--color-x)"
                      onClick={() => handleNavClick('/integrations/x')}
                    />
                    <IntegrationCard
                      name="TikTok"
                      desc="Short-form video direct-post & upload workflows"
                      tag="Content Posting"
                      color="var(--color-tiktok)"
                      onClick={() => handleNavClick('/integrations/tiktok')}
                    />
                    <IntegrationCard
                      name="YouTube"
                      desc="Supported video uploads & metadata management"
                      tag="Data API"
                      color="var(--color-youtube)"
                      onClick={() => handleNavClick('/integrations/youtube')}
                    />
                    <IntegrationCard
                      name="Pinterest"
                      desc="Image & video Pins, boards & link workflows"
                      tag="API v5"
                      color="var(--color-pinterest)"
                      onClick={() => handleNavClick('/integrations/pinterest')}
                    />
                  </div>

                  <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    <span>🛡️ All access tokens stored in AES-256-GCM encrypted credential vaults.</span>
                    <span style={{ color: '#10b981', fontWeight: 700 }}>● Multi-platform publishing powered by official platform APIs</span>
                  </div>
                </div>
              )}

              {/* 4. WHATSAPP MEGA MENU */}
              {activeMenu === 'whatsapp' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1.25fr 1.25fr 1fr', gap: '32px', alignItems: 'start' }}>
                  {/* Column 1: Conversations & Workflows */}
                  <div>
                    <div style={{ fontSize: '0.74rem', fontWeight: 800, color: 'var(--whatsapp-text)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--whatsapp-text)' }} />
                      <span>Conversations &amp; Teams</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <MegaMenuItem
                        icon={MessageSquare}
                        title="Shared Inbox"
                        desc="Collaborative customer conversations"
                        badge="Hot"
                        onClick={() => handleNavClick('/whatsapp/shared-inbox')}
                      />
                      <MegaMenuItem
                        icon={Send}
                        title="Bulk Messaging"
                        desc="Campaigns for opted-in audiences"
                        badge="Scale"
                        onClick={() => handleNavClick('/whatsapp/bulk-messaging')}
                      />
                      <MegaMenuItem
                        icon={Users}
                        title="Multi-Agent WhatsApp"
                        desc="Team collaboration"
                        onClick={() => handleNavClick('/whatsapp/multi-agent')}
                      />
                      <MegaMenuItem
                        icon={Bot}
                        title="WhatsApp Automation"
                        desc="Automated workflows"
                        onClick={() => handleNavClick('/whatsapp/automation')}
                      />
                    </div>
                  </div>

                  {/* Column 2: Leads & Platform */}
                  <div>
                    <div style={{ fontSize: '0.74rem', fontWeight: 800, color: 'var(--whatsapp-text)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--whatsapp-text)' }} />
                      <span>Pipeline &amp; Platform</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <MegaMenuItem
                        icon={UserCheck}
                        title="Lead Management"
                        desc="Organize and follow up with leads"
                        onClick={() => handleNavClick('/whatsapp/lead-management')}
                      />
                      <MegaMenuItem
                        icon={ShieldCheck}
                        title="Business API"
                        desc="Business messaging integrations"
                        badge="Official API"
                        onClick={() => handleNavClick('/whatsapp/business-api')}
                      />
                      <MegaMenuItem
                        icon={GitBranch}
                        title="CRM Integration"
                        desc="Connect customer workflows"
                        onClick={() => handleNavClick('/whatsapp/crm-integration')}
                      />
                      <MegaMenuItem
                        icon={BarChart3}
                        title="Marketing Analytics"
                        desc="Measure campaign activity"
                        onClick={() => handleNavClick('/whatsapp/marketing-analytics')}
                      />
                    </div>
                  </div>

                  {/* Column 3: Featured Spotlight Card */}
                  <div style={{ padding: '22px', borderRadius: '18px', background: 'linear-gradient(135deg, rgba(37, 211, 102, 0.12), rgba(16, 185, 129, 0.06))', border: '1px solid rgba(37, 211, 102, 0.3)' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: 'var(--radius-full)', background: 'rgba(37, 211, 102, 0.2)', marginBottom: '12px' }}>
                      <MessageCircle size={13} color="var(--whatsapp-text)" />
                      <span style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--whatsapp-text)' }}>FEATURED</span>
                    </div>
                    <h4 style={{ fontSize: '1.05rem', fontWeight: 800, margin: '0 0 8px', color: 'var(--text-primary)' }}>
                      Enterprise WhatsApp CRM Hub
                    </h4>
                    <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.55, margin: '0 0 16px' }}>
                      Bring conversations, contacts, leads, team collaboration, automation and customer workflows together in one workspace.
                    </p>
                    <button
                      onClick={() => handleNavClick('/whatsapp/crm')}
                      className="btn-magic"
                      style={{ width: '100%', padding: '9px 14px', fontSize: '0.84rem', justifyContent: 'center', backgroundColor: '#16a34a', background: 'linear-gradient(135deg, #16a34a, #0f766e)' }}
                    >
                      <span>Explore WhatsApp CRM</span>
                      <ArrowRight size={14} />
                    </button>
                    <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid rgba(37, 211, 102, 0.2)', textAlign: 'center' }}>
                      <button
                        onClick={() => handleNavClick('/whatsapp')}
                        style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', padding: 0 }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--whatsapp-text)')}
                        onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}
                      >
                        View Full WhatsApp Suite Hub →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* 5. RESOURCES MEGA MENU */}
              {activeMenu === 'resources' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.3fr 1fr', gap: '32px', alignItems: 'start' }}>
                  <div>
                    <div style={{ fontSize: '0.74rem', fontWeight: 800, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '14px' }}>
                      Learning &amp; Documentation
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <MegaMenuItem
                        icon={BookOpen}
                        title="Blog &amp; Growth Strategies"
                        desc="12 comprehensive guides for multi-platform marketing."
                        onClick={() => handleNavClick('/blog')}
                      />
                      <MegaMenuItem
                        icon={Workflow}
                        title="How It Works"
                        desc="The complete 5-step create-to-publish walkthrough."
                        onClick={() => handleNavClick('/how-it-works')}
                      />
                      <MegaMenuItem
                        icon={HelpCircle}
                        title="Frequently Asked Questions"
                        desc="Direct answers to technical and pricing inquiries."
                        onClick={() => handleNavClick('/faq')}
                      />
                      <MegaMenuItem
                        icon={FileText}
                        title="About &amp; Principles"
                        desc="Scale Bulk's engineering mission and data architecture."
                        onClick={() => handleNavClick('/about')}
                      />
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '0.74rem', fontWeight: 800, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '14px' }}>
                      Regional &amp; City Hubs (GEO)
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '0.82rem' }}>
                      {[
                        { name: 'United States', path: '/social-media-management-usa' },
                        { name: 'United Kingdom', path: '/social-media-management-uk' },
                        { name: 'Canada', path: '/social-media-management-canada' },
                        { name: 'Australia', path: '/social-media-management-australia' },
                        { name: 'India', path: '/social-media-management-india' },
                        { name: 'UAE & Middle East', path: '/social-media-management-uae' },
                        { name: 'Singapore & APAC', path: '/social-media-management-singapore' },
                        { name: 'Germany & DACH', path: '/social-media-management-germany' },
                        { name: 'New York City', path: '/social-media-management/new-york' },
                        { name: 'London', path: '/social-media-management/london' },
                        { name: 'Dubai', path: '/social-media-management/dubai' },
                        { name: 'San Francisco', path: '/social-media-management/san-francisco' },
                        { name: 'Mumbai', path: '/social-media-management/mumbai' },
                        { name: 'Bangalore', path: '/social-media-management/bangalore' }
                      ].map(hub => (
                        <button
                          key={hub.path}
                          onClick={() => handleNavClick(hub.path)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--text-secondary)',
                            textAlign: 'left',
                            padding: '6px 8px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '0.82rem',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = 'var(--primary)';
                            e.currentTarget.style.backgroundColor = 'var(--bg-glass-hover)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = 'var(--text-secondary)';
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          {hub.name} →
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{ padding: '22px', borderRadius: '18px', background: 'rgba(255, 255, 255, 0.03)', border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '8px' }}>
                      Contact &amp; Enterprise
                    </div>
                    <h4 style={{ fontSize: '0.98rem', fontWeight: 800, margin: '0 0 8px', color: 'var(--text-primary)' }}>
                      Enterprise Consultation
                    </h4>
                    <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.55, margin: '0 0 16px' }}>
                      Need custom SLAs, dedicated IP egress, or bespoke MCP agent tool pipelines?
                    </p>
                    <button
                      onClick={() => handleNavClick('/contact')}
                      className="btn-magic"
                      style={{ width: '100%', padding: '9px 14px', fontSize: '0.84rem', justifyContent: 'center' }}
                    >
                      <span>Contact Team</span>
                      <Mail size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* ========================================================================= */}
      {/* MOBILE FULLSCREEN ACCORDION DRAWER */}
      {/* ========================================================================= */}
      {mobileMenuOpen && (
        <div style={{
          position: 'fixed',
          top: 'calc(54px + env(safe-area-inset-top, 0px))',
          left: 0,
          right: 0,
          bottom: 0,
          background: 'var(--bg-primary, #07090e)',
          zIndex: 9999,
          padding: '20px clamp(16px, 4vw, 24px) calc(40px + env(safe-area-inset-bottom, 0px))',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Features Accordion */}
            <MobileAccordionItem
              title="Features"
              isOpen={mobileExpandedSection === 'features'}
              onToggle={() => setMobileExpandedSection(mobileExpandedSection === 'features' ? null : 'features')}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '6px 0 12px 12px' }}>
                <MobileSubLink label="AI Content Studio" path="/features/ai-content-studio" onClick={handleNavClick} />
                <MobileSubLink label="One-Click Publishing" path="/features/one-click-publishing" onClick={handleNavClick} />
                <MobileSubLink label="Smart Scheduling" path="/features/smart-scheduling" onClick={handleNavClick} />
                <MobileSubLink label="Multi-Channel Analytics" path="/features/multi-channel-analytics" onClick={handleNavClick} />
                <MobileSubLink label="Brand Memory Profiles" path="/features/brand-memory-profiles" onClick={handleNavClick} />
                <MobileSubLink label="Media Cloud & Drive Sync" path="/features/media-cloud-drive-sync" onClick={handleNavClick} />
                <MobileSubLink label="Developer MCP API v2" path="/developers/mcp-api-v2" onClick={handleNavClick} />
                <MobileSubLink label="All Features Overview" path="/features" onClick={handleNavClick} />
              </div>
            </MobileAccordionItem>

            {/* Solutions Accordion */}
            <MobileAccordionItem
              title="Solutions"
              isOpen={mobileExpandedSection === 'solutions'}
              onToggle={() => setMobileExpandedSection(mobileExpandedSection === 'solutions' ? null : 'solutions')}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '6px 0 12px 12px' }}>
                <MobileSubLink label="Marketing & Digital Agencies" path="/solutions/marketing-digital-agencies" onClick={handleNavClick} />
                <MobileSubLink label="Content Creators & Influencers" path="/solutions/content-creators-influencers" onClick={handleNavClick} />
                <MobileSubLink label="B2B & Enterprise Teams" path="/solutions/b2b-enterprise-teams" onClick={handleNavClick} />
                <MobileSubLink label="Client Workspace Isolation" path="/solutions/client-workspace-isolation" onClick={handleNavClick} />
                <MobileSubLink label="Small Business & D2C" path="/solutions/small-business" onClick={handleNavClick} />
              </div>
            </MobileAccordionItem>

            {/* Integrations Accordion */}
            <MobileAccordionItem
              title="Integrations (7 Channels)"
              isOpen={mobileExpandedSection === 'integrations'}
              onToggle={() => setMobileExpandedSection(mobileExpandedSection === 'integrations' ? null : 'integrations')}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '6px 0 12px 12px' }}>
                <MobileSubLink label="Instagram (Graph API)" path="/integrations/instagram" onClick={handleNavClick} />
                <MobileSubLink label="Facebook Pages" path="/integrations/facebook" onClick={handleNavClick} />
                <MobileSubLink label="LinkedIn (Posts API)" path="/integrations/linkedin" onClick={handleNavClick} />
                <MobileSubLink label="X (Twitter API v2)" path="/integrations/x" onClick={handleNavClick} />
                <MobileSubLink label="TikTok (Content Posting)" path="/integrations/tiktok" onClick={handleNavClick} />
                <MobileSubLink label="YouTube (Data API)" path="/integrations/youtube" onClick={handleNavClick} />
                <MobileSubLink label="Pinterest (API v5)" path="/integrations/pinterest" onClick={handleNavClick} />
              </div>
            </MobileAccordionItem>

            {/* WhatsApp Accordion (Dedicated Mobile Menu Section) */}
            <MobileAccordionItem
              title="WhatsApp Suite & CRM"
              isOpen={mobileExpandedSection === 'whatsapp'}
              onToggle={() => setMobileExpandedSection(mobileExpandedSection === 'whatsapp' ? null : 'whatsapp')}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '6px 0 12px 12px' }}>
                <MobileSubLink label="WhatsApp Suite Hub" path="/whatsapp" onClick={handleNavClick} />
                <MobileSubLink label="Shared Inbox" path="/whatsapp/shared-inbox" onClick={handleNavClick} />
                <MobileSubLink label="Bulk Messaging" path="/whatsapp/bulk-messaging" onClick={handleNavClick} />
                <MobileSubLink label="Multi-Agent WhatsApp" path="/whatsapp/multi-agent" onClick={handleNavClick} />
                <MobileSubLink label="WhatsApp Automation" path="/whatsapp/automation" onClick={handleNavClick} />
                <MobileSubLink label="Lead Management" path="/whatsapp/lead-management" onClick={handleNavClick} />
                <MobileSubLink label="Business API" path="/whatsapp/business-api" onClick={handleNavClick} />
                <MobileSubLink label="CRM Integration" path="/whatsapp/crm-integration" onClick={handleNavClick} />
                <MobileSubLink label="Marketing Analytics" path="/whatsapp/marketing-analytics" onClick={handleNavClick} />
                <MobileSubLink label="Enterprise WhatsApp CRM Hub" path="/whatsapp/crm" onClick={handleNavClick} />
              </div>
            </MobileAccordionItem>

            {/* Resources Accordion */}
            <MobileAccordionItem
              title="Resources & Regional Hubs"
              isOpen={mobileExpandedSection === 'resources'}
              onToggle={() => setMobileExpandedSection(mobileExpandedSection === 'resources' ? null : 'resources')}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '6px 0 12px 12px' }}>
                <MobileSubLink label="Blog & 2026 Strategy Guides" path="/blog" onClick={handleNavClick} />
                <MobileSubLink label="How It Works" path="/how-it-works" onClick={handleNavClick} />
                <MobileSubLink label="Frequently Asked Questions" path="/faq" onClick={handleNavClick} />
                <MobileSubLink label="United States Hub" path="/social-media-management-usa" onClick={handleNavClick} />
                <MobileSubLink label="United Kingdom Hub" path="/social-media-management-uk" onClick={handleNavClick} />
                <MobileSubLink label="Dubai & UAE Hub" path="/social-media-management/dubai" onClick={handleNavClick} />
                <MobileSubLink label="India Hub" path="/social-media-management-india" onClick={handleNavClick} />
                <MobileSubLink label="Canada Hub" path="/social-media-management-canada" onClick={handleNavClick} />
              </div>
            </MobileAccordionItem>

            {/* Direct Pricing Link */}
            <button
              onClick={() => handleNavClick('/pricing')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '13px 16px',
                borderRadius: 'var(--radius-md)',
                background: currentPath === '/pricing' ? 'var(--bg-glass-active)' : 'var(--bg-card)',
                color: 'var(--text-primary)',
                fontSize: '0.96rem',
                fontWeight: 700,
                border: '1px solid var(--border-color)',
                cursor: 'pointer',
                textAlign: 'left'
              }}
            >
              <span>Pricing</span>
              <ArrowRight size={15} color="var(--primary)" />
            </button>
          </div>

          {/* Mobile Footer CTAs */}
          <div style={{ marginTop: '30px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {isLoggedIn ? (
              <button
                onClick={() => handleNavClick('/dashboard')}
                className="btn-magic"
                style={{ width: '100%', padding: '13px', justifyContent: 'center', fontSize: '0.94rem' }}
              >
                <span>Go to Dashboard</span>
                <ArrowRight size={16} />
              </button>
            ) : (
              <>
                <button
                  onClick={() => { setMobileMenuOpen(false); onOpenAuth('login'); }}
                  className="btn-secondary"
                  style={{ width: '100%', padding: '12px', justifyContent: 'center', fontSize: '0.92rem' }}
                >
                  Log In
                </button>
                <button
                  onClick={() => { setMobileMenuOpen(false); onOpenAuth('register'); }}
                  className="btn-magic"
                  style={{ width: '100%', padding: '12px', justifyContent: 'center', fontSize: '0.94rem' }}
                >
                  <span>Start Free Trial</span>
                  <ArrowRight size={16} />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

interface MegaMenuItemProps {
  icon: React.ElementType;
  title: string;
  desc: string;
  badge?: string;
  onClick: () => void;
}

const MegaMenuItem: React.FC<MegaMenuItemProps> = ({ icon: Icon, title, desc, badge, onClick }) => {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '10px 12px',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.18s cubic-bezier(0.16, 1, 0.3, 1)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--bg-glass-hover)';
        e.currentTarget.style.transform = 'translateX(2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
        e.currentTarget.style.transform = 'translateX(0)';
      }}
    >
      <div style={{
        padding: '8px',
        borderRadius: '9px',
        background: 'rgba(59, 130, 246, 0.12)',
        color: 'var(--primary)',
        marginTop: '2px',
        flexShrink: 0
      }}>
        <Icon size={16} />
      </div>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)' }}>{title}</span>
          {badge && (
            <span style={{
              fontSize: '0.66rem',
              fontWeight: 800,
              padding: '2px 7px',
              borderRadius: '999px',
              background: 'rgba(59, 130, 246, 0.18)',
              color: 'var(--primary-light)',
              textTransform: 'uppercase'
            }}>
              {badge}
            </span>
          )}
        </div>
        <p style={{ fontSize: '0.76rem', color: 'var(--text-muted)', margin: '3px 0 0', lineHeight: 1.45 }}>
          {desc}
        </p>
      </div>
    </div>
  );
};

interface IntegrationCardProps {
  name: string;
  desc: string;
  tag: string;
  color: string;
  onClick: () => void;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({ name, desc, tag, color, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        textAlign: 'left',
        padding: '12px 14px',
        borderRadius: '12px',
        border: '1px solid var(--border-color)',
        background: 'rgba(255, 255, 255, 0.02)',
        cursor: 'pointer',
        transition: 'all 0.18s cubic-bezier(0.16, 1, 0.3, 1)',
        display: 'block'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--bg-glass-hover)';
        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.4)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.02)';
        e.currentTarget.style.borderColor = 'var(--border-color)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
        <strong style={{ fontSize: '0.88rem', color: 'var(--text-primary)' }}>{name}</strong>
        <span style={{
          fontSize: '0.64rem',
          fontWeight: 700,
          padding: '2px 6px',
          borderRadius: '4px',
          background: 'rgba(255, 255, 255, 0.06)',
          color: 'var(--text-muted)'
        }}>
          {tag}
        </span>
      </div>
      <p style={{ fontSize: '0.74rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.4 }}>
        {desc}
      </p>
    </button>
  );
};

interface MobileAccordionItemProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const MobileAccordionItem: React.FC<MobileAccordionItemProps> = ({ title, isOpen, onToggle, children }) => {
  return (
    <div style={{
      borderRadius: 'var(--radius-md)',
      background: 'var(--bg-card)',
      border: '1px solid var(--border-color)',
      overflow: 'hidden'
    }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '13px 16px',
          background: 'none',
          border: 'none',
          color: 'var(--text-primary)',
          fontSize: '0.96rem',
          fontWeight: 700,
          cursor: 'pointer'
        }}
      >
        <span>{title}</span>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
            color: 'var(--text-muted)'
          }}
        />
      </button>
      {isOpen && children}
    </div>
  );
};

const MobileSubLink: React.FC<{ label: string; path: string; onClick: (path: string) => void }> = ({ label, path, onClick }) => {
  return (
    <a
      href={path}
      onClick={(e) => { e.preventDefault(); onClick(path); }}
      style={{
        fontSize: '0.88rem',
        fontWeight: 600,
        color: 'var(--text-secondary)',
        textDecoration: 'none',
        padding: '4px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      <span>{label}</span>
      <ArrowRight size={13} color="var(--text-muted)" />
    </a>
  );
};
